import { Injectable, inject } from '@angular/core';
import { AppConstants } from '@core/constants';
import { TestDto } from '@core/services/test/test-dto';
import { ChartMappingData } from '@modules/apc/apc/data/chart-mapping-data';
import { ChartConstants } from '@modules/apc/apc/data/chart-modules';
import { ChartService } from '@shared/services/chart.service';
import { CustomRendererService } from '@shared/services/custom-renderer.service';
import * as _ from 'lodash-es';

declare let Bokeh_v0123: any;

interface MetabolicProfileParams {
  test: Record<string, any>;
  useDefault?: boolean;
  hideToolbar?: boolean;
  scaleText?: number;
  landscape?: boolean;
}

interface SingleMetabolicProfileParams extends MetabolicProfileParams {
  chartIndex: number;
}

const Defaults = {
  BorderColor: '#BBBBBB',
  Chart: {
    AxisLabelTextColor: '#444444',
    BackgroundFillColor: '#FFF',
    BorderFillColor: '#FFF',
    HalfWidth: 580,
    Height: 380,
    TitleColor: '#222222',
  },
  CrosshairLineColor: '#888888',
  GridLineColor: '#BBBBBB',
  LabelTextColor: '#444444',
  Legend: {
    BackgroundFillAlpha: 0.75,
    BorderWidth: 0,
  },
};

@Injectable({
  providedIn: 'root',
})
export class LoadCharacteristicsService {
  private readonly chartService: ChartService = inject(ChartService);
  private readonly customRendererService = inject(CustomRendererService);
  private readonly plottingAPI = Bokeh_v0123.Plotting;
  private readonly power_types: number[] = [
    AppConstants.REFERENCE_SYSTEM.POWER_WATT,
    AppConstants.REFERENCE_SYSTEM.POWER_WATT_KG,
  ];
  private readonly speed_types: number[] = [
    AppConstants.REFERENCE_SYSTEM.SPEED_MS,
    AppConstants.REFERENCE_SYSTEM.SPEED_KM_H,
    AppConstants.REFERENCE_SYSTEM.SPEED_MP_H,
    AppConstants.REFERENCE_SYSTEM.SPEED_MIN_SEC_KM,
    AppConstants.REFERENCE_SYSTEM.SPEED_MIN_SEC_MILE,
    AppConstants.REFERENCE_SYSTEM.SPEED_MIN_SEC_100M,
    AppConstants.REFERENCE_SYSTEM.SPEED_MIN_SEC_500M,
  ];

  destroyChart(elementId: string): void {
    this.customRendererService.emptyAndCleanupBokeh(elementId);
  }

  renderLoadCharacteristics(
    metabolicPlot2: Record<string, any>,
    test: TestDto,
    landscape = false,
    overrideElementId?: string,
    isPdfRender = false,
    scaleText?: number
  ): void {
    const elementId = overrideElementId || ChartConstants.LOAD_CHARACTERISTICS.ELEMENT_ID;

    this.customRendererService.emptyAndCleanupBokeh(elementId);
    this.generateMetabolicProfile(metabolicPlot2, elementId, {
      test,
      hideToolbar: isPdfRender,
      scaleText: 0.75,
      landscape,
    });
  }

  renderLoadCharacteristicsSingle(
    metabolicPlot2: Record<string, any>,
    test: TestDto,
    titleText: string,
    overrideElementId?: string,
    isPdfRender = false,
    scaleText?: number
  ): void {
    const elementId = overrideElementId || ChartConstants.LOAD_CHARACTERISTICS.ELEMENT_ID;

    const chartTitleTextList = [
      'Metabolic demand & VO2',
      'Lactate: recovery & accumulation',
      'Lactate – production & max. oxidation',
      'Fat & carbohydrate combustion',
      '% Aerobic & Anaerobic',
      '% Fat & Carbohydrate',
    ];
    const chartIndex = chartTitleTextList.findIndex((v) => v === titleText);
    if (chartIndex === -1) {
      console.log(`Cannot find chart index for title ${titleText}`);
      return;
    }
    const plotData = metabolicPlot2.plots[chartIndex];

    this.customRendererService.emptyAndCleanupBokeh(elementId);
    this.generateSingleMetabolicProfile(plotData, elementId, {
      test,
      chartIndex,
      hideToolbar: isPdfRender,
      scaleText: 0.75,
    });
  }

  private generateMetabolicProfile(
    plotsData: Record<string, any>,
    elementId: string,
    params: MetabolicProfileParams
  ): any[] {
    let legendData: Record<string, any>, plotReturn;
    let listPlots: Record<string, any>[] = [];
    const legends: any[] = [];

    const chart = {
      plot_width: Defaults.Chart.HalfWidth,
      plot_height: Defaults.Chart.Height,
      tools: 'pan,box_zoom,wheel_zoom,crosshair',
      x_axis_type: plotsData['x_axis_type'],
      toolbar_location: 'above',
      background_fill_color: Defaults.Chart.BackgroundFillColor,
      border_fill_color: Defaults.Chart.BorderFillColor,
      outline_line_color: Defaults.BorderColor,
    };

    _.each(plotsData.plots, (plotData: Record<string, any>, chartIndex: number): void => {
      let x_range = listPlots[chartIndex - 1] ? listPlots[chartIndex - 1].x_range : undefined;
      let extra_x_ranges = listPlots[chartIndex - 1] ? listPlots[chartIndex - 1].extra_x_ranges : undefined;
      if (chartIndex === 5 || chartIndex === 4) {
        let min_data: number = Math.min(...plotData.data_df.x);
        let max_data: number = Math.max(...plotData.data_df.x);
        x_range = [min_data, max_data];
        extra_x_ranges = {
          primary: new Bokeh_v0123.Range1d({
            start: min_data,
            end: max_data,
          }),
          secondary: new Bokeh_v0123.Range1d({
            start: min_data,
            end: max_data,
          }),
        };
      }

      if (chartIndex == 3) {
        plotReturn = this.generateBokehPlotMPFatChart(
          plotData,
          chart,
          x_range,
          'metabolic_profile',
          params.test.id,
          params.useDefault,
          chartIndex,
          params.test,
          extra_x_ranges,
          true,
          params.scaleText
        );
      } else {
        plotReturn = this.generateBokehPlot(
          plotData,
          chart,
          x_range,
          'metabolic_profile',
          params.test.id,
          params.useDefault,
          chartIndex,
          params.test,
          extra_x_ranges,
          true,
          params.scaleText
        );
      }

      plotReturn.add_tools(
        new Bokeh_v0123.CrosshairTool({
          line_color: Defaults.CrosshairLineColor,
        })
      );

      const attr = {
        id: plotReturn.id,
        name: plotData.data_names[0],
        width: plotReturn.attributes.width,
        valueText: plotData['x_axis_label'],
        action: plotData['x_legend'],
      };

      // Callback for x range
      const xRangeCallbackCode: string = 'let data = this.attributes.args.data; saveSettingsForMP(data, cb_obj);';
      plotReturn.x_range.callback = new Bokeh_v0123.CustomJS({
        code: xRangeCallbackCode,
        args: new Bokeh_v0123.ColumnDataSource({
          data: plotData.data_df,
        }),
      });

      // Callback for y range
      let yRangeCallbackCode: string = 'let data = this.attributes.args.chart_index; saveYSettingsForMP(data, cb_obj);';
      plotReturn.y_range.callback = new Bokeh_v0123.CustomJS({
        code: yRangeCallbackCode,
        args: {
          chart_index: chartIndex,
        },
      });

      // Callback for extra y ranges (only for MPFatChart)
      if (chartIndex === 3 && plotReturn?.extra_y_ranges?.y1) {
        let extraYRangeCallbackCode: string =
          'let data = this.attributes.args.chart_index; saveExtraYSettingsForMP(data, "y1", cb_obj);';
        plotReturn.extra_y_ranges.y1.callback = new Bokeh_v0123.CustomJS({
          code: extraYRangeCallbackCode,
          args: {
            chart_index: chartIndex,
          },
        });
      }

      legendData = this.createLegend(attr);
      if (plotData?.data_names?.length) {
        legendData.dataInfo = [];
        if (elementId.indexOf('_rp') == -1) {
          legendData = this.generateLegendInfoTag(plotData, legendData);
        }
      }

      const test = params.test;
      if (
        plotData.data_df.power &&
        plotData.data_df.power[0] == null &&
        test.sport.simulation_type == AppConstants.SIMULATE_TYPE.SPEED &&
        (_.indexOf(this.power_types, test.sport.primary_type) != -1 ||
          _.indexOf(this.power_types, test.sport.secondary_type) != -1)
      ) {
        legendData.isMissingPower = true;
      } else if (
        plotData.data_df.vms &&
        plotData.data_df.vms[0] == null &&
        test.sport.simulation_type == AppConstants.SIMULATE_TYPE.POWER &&
        (_.indexOf(this.speed_types, test.sport.primary_type) != -1 ||
          _.indexOf(this.speed_types, test.sport.secondary_type) != -1)
      ) {
        legendData.isMissingSpeed = true;
      }

      this.generateUniqueDataComponentValue(legendData, plotData.title_text);

      legends.push(legendData);
      plotReturn.title.render_mode = 'css';
      listPlots.push(plotReturn);
    });

    const gridPlots = this.createGridPlot(listPlots, params.landscape);
    let gridPlotOptions: Record<string, any> = { sizing_mode: 'scale_width' };
    if (params.hideToolbar) {
      gridPlotOptions.toolbar_location = null;
    }
    let combinedChart = this.plottingAPI.gridplot(gridPlots, gridPlotOptions);
    this.plottingAPI.show(combinedChart, elementId);
    this.splitUpChartInfoLayout(elementId);

    return legends;
  }

  private generateSingleMetabolicProfile(
    plotData: Record<string, any>,
    elementId: string,
    params: SingleMetabolicProfileParams
  ): any[] {
    let legendData: Record<string, any>, plotReturn;
    let listPlots: Record<string, any>[] = [];
    const legends = [];

    const chart = {
      plot_width: Defaults.Chart.HalfWidth,
      plot_height: Defaults.Chart.Height,
      tools: 'pan,box_zoom,wheel_zoom,crosshair',
      x_axis_type: plotData['x_axis_type'],
      toolbar_location: 'above',
      background_fill_color: Defaults.Chart.BackgroundFillColor,
      border_fill_color: Defaults.Chart.BorderFillColor,
      outline_line_color: Defaults.BorderColor,
    };

    const { chartIndex } = params;

    let x_range = listPlots[chartIndex - 1] ? listPlots[chartIndex - 1].x_range : undefined;
    let extra_x_ranges = listPlots[chartIndex - 1] ? listPlots[chartIndex - 1].extra_x_ranges : undefined;
    if (chartIndex === 5 || chartIndex === 4) {
      let min_data: number = Math.min(...plotData.data_df.x);
      let max_data: number = Math.max(...plotData.data_df.x);
      x_range = [min_data, max_data];
      extra_x_ranges = {
        primary: new Bokeh_v0123.Range1d({
          start: min_data,
          end: max_data,
        }),
        secondary: new Bokeh_v0123.Range1d({
          start: min_data,
          end: max_data,
        }),
      };
    }

    if (chartIndex == 3) {
      plotReturn = this.generateBokehPlotMPFatChart(
        plotData,
        chart,
        x_range,
        'metabolic_profile',
        params.test.id,
        params.useDefault,
        chartIndex,
        params.test,
        extra_x_ranges,
        true,
        params.scaleText
      );
    } else {
      plotReturn = this.generateBokehPlot(
        plotData,
        chart,
        x_range,
        'metabolic_profile',
        params.test.id,
        params.useDefault,
        chartIndex,
        params.test,
        extra_x_ranges,
        true,
        params.scaleText
      );
    }

    plotReturn.add_tools(
      new Bokeh_v0123.CrosshairTool({
        line_color: Defaults.CrosshairLineColor,
      })
    );

    const attr = {
      id: plotReturn.id,
      name: plotData.data_names[0],
      width: plotReturn.attributes.width,
      valueText: plotData['x_axis_label'],
      action: plotData['x_legend'],
    };

    // Callback for x range
    const xRangeCallbackCode: string = 'let data = this.attributes.args.data; saveSettingsForMP(data, cb_obj);';
    plotReturn.x_range.callback = new Bokeh_v0123.CustomJS({
      code: xRangeCallbackCode,
      args: new Bokeh_v0123.ColumnDataSource({
        data: plotData.data_df,
      }),
    });

    // Callback for y range
    let yRangeCallbackCode: string = 'let data = this.attributes.args.chart_index; saveYSettingsForMP(data, cb_obj);';
    plotReturn.y_range.callback = new Bokeh_v0123.CustomJS({
      code: yRangeCallbackCode,
      args: {
        chart_index: chartIndex,
      },
    });

    // Callback for extra y ranges (only for MPFatChart)
    if (chartIndex === 3 && plotReturn?.extra_y_ranges?.y1) {
      let extraYRangeCallbackCode: string =
        'let data = this.attributes.args.chart_index; saveExtraYSettingsForMP(data, "y1", cb_obj);';
      plotReturn.extra_y_ranges.y1.callback = new Bokeh_v0123.CustomJS({
        code: extraYRangeCallbackCode,
        args: {
          chart_index: chartIndex,
        },
      });
    }

    legendData = this.createLegend(attr);
    if (plotData?.data_names?.length) {
      legendData.dataInfo = [];
      if (elementId.indexOf('_rp') == -1) {
        legendData = this.generateLegendInfoTag(plotData, legendData);
      }
    }

    const test = params.test;
    if (
      plotData.data_df.power &&
      plotData.data_df.power[0] == null &&
      test.sport.simulation_type == AppConstants.SIMULATE_TYPE.SPEED &&
      (_.indexOf(this.power_types, test.sport.primary_type) != -1 ||
        _.indexOf(this.power_types, test.sport.secondary_type) != -1)
    ) {
      legendData.isMissingPower = true;
    } else if (
      plotData.data_df.vms &&
      plotData.data_df.vms[0] == null &&
      test.sport.simulation_type == AppConstants.SIMULATE_TYPE.POWER &&
      (_.indexOf(this.speed_types, test.sport.primary_type) != -1 ||
        _.indexOf(this.speed_types, test.sport.secondary_type) != -1)
    ) {
      legendData.isMissingSpeed = true;
    }

    this.generateUniqueDataComponentValue(legendData, plotData.title_text);

    legends.push(legendData);
    plotReturn.title.render_mode = 'css';
    listPlots.push(plotReturn);

    const gridPlots = this.createGridPlot(listPlots);
    let gridPlotOptions: Record<string, any> = { sizing_mode: 'scale_width' };
    if (params.hideToolbar) {
      gridPlotOptions.toolbar_location = null;
    }
    let combinedChart = this.plottingAPI.gridplot(gridPlots, gridPlotOptions);
    this.plottingAPI.show(combinedChart, elementId);
    this.splitUpChartInfoLayout(elementId);

    return legends;
  }

  private generateBokehPlot(
    data: any = null,
    chart: any = null,
    x_range: any = null,
    type: any = null,
    testId: any = null,
    useDefault: any = null,
    chart_index: any = null,
    test: any = null,
    extra_x_ranges: any = null,
    has_secondary_x: boolean = true,
    scaleText = 1
  ) {
    let glyphLine: {
      attributes: { glyph: { line_dash: number[] } };
      y_range_name: any;
    };
    this.handelXRangeBokehPlot(x_range, chart, data, type, useDefault, testId);
    this.handelYRangeBokehPlot(chart, data, type, useDefault, testId, chart_index);

    if (data.extra_y_ranges) {
      chart.extra_y_ranges = this.createExtraRange(data.extra_y_ranges);
    }

    this.initExtraXRanges(extra_x_ranges, chart, test, has_secondary_x);

    chart = this.createFigureChart(chart);

    this.mappingDataDF(test, data);
    this.addSecondaryXAxis(test, chart, data.data_df, has_secondary_x, scaleText);

    if (data?.lines?.length) {
      _.each(data.lines, (line): void => {
        let objectReturn = this.createLine(line, chart);
        chart = objectReturn.chart;
        glyphLine = objectReturn.glyph;
        // Manually override the line in glyph object attributes based on line-dash attributes
        // due to BokehJS not supported for line_dash attribute
        if (line.line_dash == 'dashed') {
          glyphLine.attributes.glyph.line_dash = [5, 6];
        }
        if (line.line_dash == 'dotted') {
          glyphLine.attributes.glyph.line_dash = [2, 4];
        }

        glyphLine.y_range_name = line.y_range_name;
      });
    }

    if (data?.layouts?.length) {
      _.each(data.layouts, (layout): void => {
        chart = this.createLayout(layout, chart);
      });
    }

    if (data?.patches?.length) {
      _.each(data.patches, (patch): void => {
        if (patch.line_alpha == null) {
          chart.patch({
            x: patch.x,
            y: patch.y,
            legend: patch.legend,
            fill_alpha: patch.fill_alpha,
            line_width: patch.line_width,
            fill_color: patch.fill_color,
          });
        } else {
          chart.patch({
            x: patch.x,
            y: patch.y,
            legend: patch.legend,
            fill_alpha: patch.fill_alpha,
            line_alpha: patch.line_alpha,
            line_width: patch.line_width,
            fill_color: patch.fill_color,
          });
        }
      });
    }

    if (data.data_df) {
      chart = this.addCallBackHoverEventToChartMetabolicProfile(data.data_df, chart, test);
    }
    chart = this.postRenderChartLayout(chart, data, scaleText);

    if (test) {
      chart.xaxis.visible = false;
    }

    return chart;
  }

  private generateBokehPlotMPFatChart(
    data: any,
    chart: any,
    x_range: any = null,
    type: any = null,
    testId: any = null,
    useDefault: any = null,
    chart_index: any = null,
    test: any = null,
    extra_x_ranges: any = null,
    has_secondary_x: boolean = true,
    scaleText = 1
  ) {
    let glyphLine: any;
    this.handleXRangeBokehPlotMPFatChar(x_range, chart, data, type, useDefault, testId);
    this.handleYRangeBokehPlotMPFatChar(data, type, useDefault, chart_index, testId, chart);

    if (data.extra_y_ranges) {
      if (type && !useDefault) {
        // //localStorage.setItem('defaultMPsettings', JSON.stringify(data.x_range));
        let extraYVal: string | null = localStorage.getItem(`MPYsettings${chart_index}_y1_${testId}`);

        if (extraYVal) {
          data.extra_y_ranges.y1 = JSON.parse(extraYVal);
        }
      }

      chart.extra_y_ranges = this.createExtraRange(data.extra_y_ranges);
    }

    this.initExtraXRanges(extra_x_ranges, chart, test, has_secondary_x);

    chart = this.createFigureChart(chart);

    this.mappingDataDF(test, data);
    this.addSecondaryXAxis(test, chart, data.data_df, has_secondary_x, scaleText);

    if (data?.lines?.length && data?.patches?.length) {
      glyphLine = this.handelDrawBokehPlotMPFatChar(data, chart);
    } else {
      let chartData = this.drawLinesPatches(data, chart);
      chart = chartData.chart;
      glyphLine = chartData.glyphLine;
    }

    if (data.layouts && data.layouts.length > 0) {
      _.each(data.layouts, (layout): void => {
        chart = this.createLayout(layout, chart);
      });
    }

    if (data.data_df && !test) {
      chart = this.addCallBackHoverEventToChart(data.data_df, chart);
    } else {
      chart = this.addCallBackHoverEventToChartMetabolicProfile(data.data_df, chart, test);
    }

    chart = this.postRenderChartLayout(chart, data, scaleText);

    if (test) {
      // Hide original X axis
      chart.xaxis.visible = false;
    }

    return chart;
  }

  private handelXRangeBokehPlot(
    x_range: any,
    chart: any,
    data: any,
    type: any,
    useDefault: boolean,
    testId: any
  ): void {
    if (x_range) {
      chart.x_range = x_range;
    } else {
      if (data.x_range) {
        //Firstly test with metabolic profile chart
        if (type && type == 'metabolic_profile' && !useDefault) {
          // //localStorage.setItem('defaultMPsettings', JSON.stringify(data.x_range));
          const xVal: string | null = localStorage.getItem('MPXsettings_' + testId);
          if (xVal) {
            data.x_range = JSON.parse(xVal);
          }
        }
        chart.x_range = this.createRange(data.x_range);
      }
    }
  }

  private handelYRangeBokehPlot(
    chart: any,
    data: any,
    type: any,
    useDefault: boolean,
    testId: any,
    chart_index: any
  ): void {
    if (data.y_range) {
      //Firstly test with metabolic profile chart
      if (type && type == 'metabolic_profile' && !useDefault) {
        // //localStorage.setItem('defaultMPsettings', JSON.stringify(data.x_range));
        let yVal: string | null = localStorage.getItem(`MPYsettings${chart_index}_${testId}`);
        if (yVal) {
          data.y_range = JSON.parse(yVal);
        }
      }
      chart.y_range = this.createRange(data.y_range);
    }
  }

  private handleXRangeBokehPlotMPFatChar(
    x_range: any,
    chart: any,
    data: any,
    type: any,
    useDefault: any,
    testId: any
  ): void {
    if (x_range) {
      chart.x_range = x_range;
    } else {
      if (data.x_range) {
        //Firstly test with metabolic profile chart
        if (type && !useDefault) {
          // //localStorage.setItem('defaultMPsettings', JSON.stringify(data.x_range));
          let xVal: string | null = localStorage.getItem('MPXsettings_' + testId);
          if (xVal) {
            data.x_range = JSON.parse(xVal);
          }
        }
        chart.x_range = this.createRange(data.x_range);
      }
    }
  }

  private handleYRangeBokehPlotMPFatChar(
    data: any,
    type: any,
    useDefault: any,
    chart_index: any,
    testId: any,
    chart: any
  ): void {
    if (data.y_range) {
      //Firstly test with metabolic profile chart
      if (type && !useDefault) {
        // //localStorage.setItem('defaultMPsettings', JSON.stringify(data.x_range));
        let yVal: string | null = localStorage.getItem(`MPYsettings${chart_index}_${testId}`);
        if (yVal) {
          data.y_range = JSON.parse(yVal);
        }
      }
      chart.y_range = this.createRange(data.y_range);
    }
  }

  private handelDrawBokehPlotMPFatChar(data: any, chart: any): void {
    let maxLength: number = 0;
    let linesLength = data.lines.length;
    let patchesLength = data.lines.length;
    let glyphLine;
    if (linesLength >= patchesLength) {
      maxLength = linesLength;
    } else {
      maxLength = patchesLength;
    }

    for (let i = 0; i < maxLength; i++) {
      let line = data.lines[i];
      if (!line) {
        return;
      }
      let objectReturn = this.createLine(line, chart);
      chart = objectReturn.chart;
      glyphLine = objectReturn.glyph;
      // Manually override the line in glyph object attributes based on line-dash attributes
      // due to BokehJS not supported for line_dash attribute
      if (line.line_dash == 'dashed') {
        glyphLine.attributes.glyph.line_dash = [5, 6];
      }
      if (line.line_dash == 'dotted') {
        glyphLine.attributes.glyph.line_dash = [2, 4];
      }

      glyphLine.y_range_name = line.y_range_name;

      let patch = data.patches[i];
      if (patch) {
        if (patch.line_alpha == null) {
          chart.patch({
            x: patch.x,
            y: patch.y,
            legend: patch.legend,
            fill_alpha: patch.fill_alpha,
            line_width: patch.line_width,
            fill_color: patch.fill_color,
          });
        } else {
          chart.patch({
            x: patch.x,
            y: patch.y,
            legend: patch.legend,
            fill_alpha: patch.fill_alpha,
            line_alpha: patch.line_alpha,
            line_width: patch.line_width,
            fill_color: patch.fill_color,
          });
        }
      }
    }
  }

  private drawLinesPatches(data: any, chart: any) {
    let glyphLine;
    if (data.lines && data.lines.length > 0) {
      _.each(data.lines, (line): void => {
        const objectReturn = this.createLine(line, chart);
        chart = objectReturn.chart;
        glyphLine = objectReturn.glyph;
        // Manually override the line in glyph object attributes based on line-dash attributes
        // due to BokehJS not supported for line_dash attribute
        if (line.line_dash == 'dashed') {
          glyphLine.attributes.glyph.line_dash = [5, 6];
        }
        if (line.line_dash == 'dotted') {
          glyphLine.attributes.glyph.line_dash = [2, 4];
        }

        glyphLine.y_range_name = line.y_range_name;
      });
    }

    if (data.patches && data.patches.length > 0) {
      _.each(data.patches, (patch): void => {
        if (patch.line_alpha == null) {
          chart.patch({
            x: patch.x,
            y: patch.y,
            legend: patch.legend,
            fill_alpha: patch.fill_alpha,
            fill_color: patch.fill_color,
            line_width: patch.line_width,
          });
        } else {
          chart.patch({
            x: patch.x,
            y: patch.y,
            legend: patch.legend,
            fill_alpha: patch.fill_alpha,
            line_alpha: patch.line_alpha,
            line_width: patch.line_width,
            fill_color: patch.fill_color,
          });
        }
      });
    }
    return {
      chart: chart,
      glyphLine: glyphLine,
    };
  }

  private createRange(range: { plot_range: any[]; bounds: any }) {
    return new Bokeh_v0123.Range1d({
      start: range.plot_range[0],
      end: range.plot_range[1],
      bounds: range.bounds,
    });
  }

  private createExtraRange(range: any) {
    let extradr: any = {};
    for (let range_name in range) {
      if (range.hasOwnProperty(range_name)) {
        // IAT-2354 Use default value [0,100], not depending on plots data
        // extradr[range_name] = new Bokeh_v0123.Range1d({
        //   start: ChartConstants.RANGE.START,
        //   end: ChartConstants.RANGE.END,
        //   // Remove bounding to NOT prevent the user from
        //   // panning/zooming/etc
        //   // bounds: range[range_name].bounds,
        // });

        extradr[range_name] = new Bokeh_v0123.Range1d({
          start: range[range_name].plot_range[0],
          end: range[range_name].plot_range[1],
          bounds: range[range_name].bounds,
        });
      }
    }

    return extradr;
  }

  private initExtraXRanges(extra_x_ranges: any, chart: any, test: any, has_secondary_x: boolean = true): void {
    if (extra_x_ranges) {
      chart.extra_x_ranges = extra_x_ranges;
    } else {
      chart.extra_x_ranges = {
        primary: new Bokeh_v0123.Range1d({
          start: chart.x_range.min,
          end: chart.x_range.max,
        }),
      };

      if (test?.sport?.secondary_type && has_secondary_x) {
        chart.extra_x_ranges['secondary'] = new Bokeh_v0123.Range1d({
          start: chart.x_range.min,
          end: chart.x_range.max,
        });
      }
    }
  }

  private createFigureChart(attrs: any) {
    return this.plottingAPI.figure(attrs);
  }

  private mappingDataDF(test: any, data: any): void {
    if (!test) {
      return;
    }

    if (!data.data_df) {
      data.data_df = {};
    }

    if (test.data_df_power) {
      data.data_df['power'] = test.data_df_power;
    }

    if (test.data_df_vms) {
      data.data_df['vms'] = test.data_df_vms;
    }

    if (!data.data_df['x']) {
      if (test.sport.simulation_type == AppConstants.SIMULATE_TYPE.POWER) {
        data.data_df['x'] = test.data_df_power;
      } else if (test.sport.simulation_type == AppConstants.SIMULATE_TYPE.SPEED) {
        data.data_df['x'] = test.data_df_vms;
      }
    }
  }

  private addSecondaryXAxis(test: any, chart: any, data: any, has_secondary_x: boolean = true, scaleText = 1): void {
    if (test) {
      const primary_label: string = this.findXAxisLabel(test.sport.primary_type);
      const secondary_label: string = this.findXAxisLabel(test.sport.secondary_type);
      let fontSize = 10;
      if (scaleText != null && scaleText !== 1) {
        fontSize = fontSize * scaleText;
      }
      const common_layout = {
        axis_label_text_font_size: `${fontSize}pt`,
        axis_label_text_font_style: 'normal',
        axis_label_text_color: Defaults.LabelTextColor,
        major_label_text_color: Defaults.LabelTextColor,
      };
      let primary_layout: any = {
        x_range_name: 'primary',
        axis_label: primary_label,
        ...common_layout,
        formatter: new Bokeh_v0123.FuncTickFormatter({
          code: `
          let listDataX = ${JSON.stringify(data)};
          return calculateTicker(tick, ${test.sport.primary_type}, ${
            test.sport.simulation_type
          }, ${test.mass}, listDataX);`,
        }),
      };

      chart.add_layout(new Bokeh_v0123.LinearAxis(primary_layout), 'below');

      if (test?.sport?.secondary_type && has_secondary_x) {
        let secondary_layout: any = {
          x_range_name: 'secondary',
          axis_label: secondary_label,
          ...common_layout,
          formatter: new Bokeh_v0123.FuncTickFormatter({
            code: `
            let listDataX = ${JSON.stringify(data)};
            return calculateTicker(tick, ${test.sport.secondary_type}, ${
              test.sport.simulation_type
            }, ${test.mass}, listDataX);`,
          }),
        };

        chart.add_layout(new Bokeh_v0123.LinearAxis(secondary_layout), 'below');
      }
    }
  }

  private findXAxisLabel(id: number): string {
    const s_power = _.find(AppConstants.REFERENCE_SYSTEMS_POWER, { id: id });
    const s_speed = _.find(AppConstants.REFERENCE_SYSTEMS_SPEED, { id: id });
    let label: string = '';
    if (s_power) {
      label = s_power.unit;
    } else if (s_speed) {
      label = s_speed.unit;
    }
    return label;
  }

  private createLine(
    line: {
      source: { [x: string]: any };
      x: string | number;
      y: string | number;
      line_color: any;
      line_dash: any;
      legend: any;
      alpha: any;
    },
    chart: {
      line: (arg0: {
        x: { field: string };
        y: { field: string };
        source: any;
        line_width: number;
        line_color: any;
        line_dash: any[];
        legend: any;
        alpha: any;
      }) => any;
    }
  ) {
    let glyph = chart.line({
      x: { field: 'x' },
      y: { field: 'y' },
      source: new Bokeh_v0123.ColumnDataSource({
        data: {
          x: line.source[line.x], //.map(function(val) { return val == null ? NaN : val }),
          y: line.source[line.y], //.map(function(val) { return val == null ? NaN : val })
        },
      }),
      line_width: 2,
      line_color: line.line_color,
      line_dash: [line.line_dash],
      legend: line.legend,
      alpha: line.alpha,
    });

    return {
      chart: chart,
      glyph: glyph,
    };
  }

  private createLayout(layout: any, chart: any) {
    for (let type in layout) {
      if (type != 'axis_position' && layout.hasOwnProperty(type)) {
        if (layout[type] != null) {
          switch (type) {
            case 'linear_axis':
              chart = this.createLinearAxis(layout[type], chart, layout['axis_position']);
              break;
          }
        }
      }
    }

    return chart;
  }

  private createLinearAxis(
    layout: {
      y_range_name: any;
      axis_label: any;
      axis_label_text_font_size: any;
    },
    chart: { add_layout: (arg0: any, arg1: any) => void },
    position: any
  ) {
    chart.add_layout(
      new Bokeh_v0123.LinearAxis({
        y_range_name: layout.y_range_name,
        axis_label: layout.axis_label,
        axis_label_text_font_size: layout.axis_label_text_font_size,
      }),
      position
    );

    return chart;
  }

  private addCallBackHoverEventToChartMetabolicProfile(
    data: any,
    chart: { id: any; add_tools: (arg0: any) => void },
    test: any = null
  ) {
    if (test) {
      const primary_unit: string = this.findXAxisLabel(test.sport.primary_type);
      const secondary_unit: string = this.findXAxisLabel(test.sport.secondary_type);
      let test_data = _.cloneDeep(test);
      test_data.primary_unit = primary_unit;
      test_data.secondary_unit = secondary_unit;

      // Debounce the callback
      const debouncedCallbackCode: string = `
        if (!window.lastHoverUpdate) {
          window.lastHoverUpdate = 0;
        }
        const now = performance.now();
        if (now - window.lastHoverUpdate < 16) {
          return;
        }
        window.lastHoverUpdate = now;
        
        let data = this.attributes.args.data.data;
        let id = this.attributes.id;
        let geometry = cb_data["geometry"];
        let test = this.attributes.args.data.test_data;
        bokeh_callback(data, geometry, id, test);
      `;

      let code: string =
        'let data = this.attributes.args.data.data; let id = this.attributes.id ;let geometry = cb_data["geometry"]; let test = this.attributes.args.data.test_data; bokeh_callback(data, geometry, id, test);';

      let callback = new Bokeh_v0123.CustomJS({
        id: chart.id,
        code: debouncedCallbackCode,
        args: new Bokeh_v0123.ColumnDataSource({
          data: {
            data,
            test_data,
          },
        }),
      });

      chart.add_tools(
        new Bokeh_v0123.HoverTool({
          callback: callback,
        })
      );
      return chart;
    } else {
      return this.addCallBackHoverEventToChart(data, chart);
    }
  }

  private addCallBackHoverEventToChart(data: any, chart: { id: any; add_tools: (arg0: any) => void }) {
    let code: string =
      'let data = this.attributes.args.data; let id = this.attributes.id ;let geometry = cb_data["geometry"]; bokeh_callback(data, geometry, id);';
    let callback = new Bokeh_v0123.CustomJS({
      id: chart.id,
      code: code,
      args: new Bokeh_v0123.ColumnDataSource({
        data: data,
      }),
    });

    chart.add_tools(
      new Bokeh_v0123.HoverTool({
        callback: callback,
      })
    );

    return chart;
  }

  private createLegend(attr: any) {
    return {
      lines: [],
      id: attr.id,
      dataName: attr.dataName,
      style: {
        width: attr.width,
      },
      x: {
        valueText: attr.valueText,
        action: attr.action,
        name: 'x_disp',
        style: {
          color: '#000000',
        },
      },
      isMissingPower: false,
      isMissingSpeed: false,
    };
  }

  private generateLegendInfoTag(plotData: Record<string, any>, legendData: Record<string, any>) {
    let same_legend_lines: { legend: any }[] = _.uniqBy(plotData.lines, (line: { legend: any }) => {
      return line.legend;
    });

    _.each(plotData.data_names, (name, idx: string): void => {
      legendData.dataInfo[idx] = {
        name: name,
        index: idx,
        typeString: '___',
        typeClass: 'continued_line',
        lines: [],
      };
      _.each(same_legend_lines, (lineLegend: any): void => {
        let text: string = '';
        if (lineLegend.y_range_name == 'default') {
          text = plotData['y_axis_label'];
        } else {
          for (let row of plotData['layouts']) {
            if (row.linear_axis.y_range_name == lineLegend.y_range_name) {
              text = row.linear_axis.axis_label;
            }
          }
        }
        let infoData = this.createLegendInfo(legendData, lineLegend, ChartMappingData, text);
        legendData.dataInfo[idx].lines.push(infoData);

        _.each(plotData.lines, (sameLine) => {
          if (
            lineLegend.line_color == sameLine.line_color &&
            lineLegend.legend != sameLine.legend &&
            sameLine.line_dash &&
            sameLine.line_dash != 'solid'
          ) {
            legendData.dataInfo[idx].typeClass = sameLine.line_dash + '_line';
            legendData.dataInfo[idx].typeString = this.getTypeStringLegendInfoTag(sameLine);
          }
        });
      });
    });

    return legendData;
  }

  private getTypeStringLegendInfoTag(sameLine: any): string {
    return sameLine.line_dash == 'dashed' ? '---' : '...';
  }

  private createLegendInfo(legendData: any, plot: { y: any; line_dash: any }, configData: any, labelText: any) {
    let info: any[] = _.filter(configData, (data) => {
      return data.name == plot.y;
    });

    return {
      id: plot.y,
      legendText: info[0].legend,
      valueText: labelText,
      style: { color: this.colorToHex(info[0].color) },
      type: plot.line_dash,
    };
  }

  private colorToHex(color: any): string {
    // Convert any CSS color to a hex representation
    // Examples:
    // colorToHex('red')            # '#ff0000'
    // colorToHex('rgb(255, 0, 0)') # '#ff0000'
    let rgba: any, hex: any;
    rgba = this.colorToRGBA(color);
    hex = [0, 1, 2]
      .map((idx: number) => {
        return this.byteToHex(rgba[idx]);
      })
      .join('');

    return '#' + hex;
  }

  private colorToRGBA(color: any): Uint8ClampedArray | null {
    // Returns the color as an array of [r, g, b, a] -- all range from 0 - 255
    // color must be a valid canvas fillStyle. This will cover most anything
    // you'd want to use.
    // Examples:
    // colorToRGBA('red')  # [255, 0, 0, 255]
    // colorToRGBA('#f00') # [255, 0, 0, 255]
    let cvs, ctx;
    cvs = document.createElement('canvas');
    cvs.height = 1;
    cvs.width = 1;
    ctx = cvs.getContext('2d');
    if (ctx) {
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, 1, 1);

      return ctx.getImageData(0, 0, 1, 1).data;
    } else {
      return null;
    }
  }

  private byteToHex(num: any): string {
    // Turns a number (0-255) into a 2-character hex number (00-ff)
    return ('0' + num.toString(16)).slice(-2);
  }

  private generateUniqueDataComponentValue(legendData: Record<string, any>, graphTitle: string): void {
    const metabolicDemandVO2 = 'Metabolic demand & VO2';
    const metabolicDemandVO2AttrValue = 'metabolic-demand-VO2';
    const lactateRecoveryAccumulation = 'Lactate: recovery & accumulation';
    const lactateRecoveryAccumulationAttrValue = 'lactate-recovery-accumulation';
    const lactateProductionMaxOxidation = 'Lactate – production & max. oxidation';
    const lactateProductionMaxOxidationAttrValue = 'lactate-production-max-oxidation';
    const fatCarbohydrateCombustion = 'Fat & carbohydrate combustion';
    const fatCarbohydrateCombustionAttrValue = 'fat-carbohydrate-combustion';
    const aerobicAnaerobic = '% Aerobic & Anaerobic';
    const aerobicAnaerobicAttrValue = 'aerobic-anaerobic';
    const fatCarbohydrate = '% Fat & Carbohydrate';
    const fatCarbohydrateAttrValue = 'fat-arbohydrate';

    switch (graphTitle) {
      case metabolicDemandVO2:
        legendData.dataComponent = metabolicDemandVO2AttrValue;
        break;
      case lactateRecoveryAccumulation:
        legendData.dataComponent = lactateRecoveryAccumulationAttrValue;
        break;
      case lactateProductionMaxOxidation:
        legendData.dataComponent = lactateProductionMaxOxidationAttrValue;
        break;
      case fatCarbohydrateCombustion:
        legendData.dataComponent = fatCarbohydrateCombustionAttrValue;
        break;
      case aerobicAnaerobic:
        legendData.dataComponent = aerobicAnaerobicAttrValue;
        break;
      case fatCarbohydrate:
        legendData.dataComponent = fatCarbohydrateAttrValue;
        break;
    }
  }

  private createGridPlot(listPlots: Record<string, any>[], landscape = false): Record<string, any>[][] {
    let gridPlots: any[] = [],
      plot1,
      plot2,
      plot3,
      plotArray;

    if (landscape) {
      for (let j = 0; j < listPlots.length; j += 3) {
        plotArray = [];
        plot1 = listPlots[j];
        plot2 = listPlots[j + 1];
        plot3 = listPlots[j + 2];
        plotArray.push(plot1);
        if (plot2) {
          plotArray.push(plot2);
        }
        if (plot3) {
          plotArray.push(plot3);
        }
        gridPlots.push(plotArray);
      }
    } else {
      for (let j = 0; j < listPlots.length; j += 2) {
        plotArray = [];
        plot1 = listPlots[j];
        plot2 = listPlots[j + 1];
        plotArray.push(plot1);
        if (plot2) {
          plotArray.push(plot2);
        }
        gridPlots.push(plotArray);
      }
    }

    return gridPlots;
  }

  private splitUpChartInfoLayout(elementId: any): void {
    setTimeout((): void => {
      const targetElement: Element | null = this.customRendererService.select(elementId);
      const childNodeList: NodeListOf<Element> | undefined = this.customRendererService.getChildNodes(
        targetElement,
        "div[id*='modelid_Row-']"
      );
      let chartWidth: string = '500';

      childNodeList?.forEach((ele: Element): void => {
        const appendIndex: number[] = [];
        const elementList: NodeListOf<ChildNode> = ele.childNodes;
        elementList.forEach((element: any): void => {
          chartWidth = String(element.offsetWidth);
          const childId = element.id.split('modelid_')[1];
          const legends: NodeListOf<HTMLElement> = this.customRendererService.selectAll('.bokeh-legend');
          legends.forEach((legend: any, index: number): void => {
            if (legend.id == childId) {
              appendIndex.push(index);
            }
          });
        });

        appendIndex.forEach((indexValue: number): void => {
          let legendEle: HTMLElement = this.customRendererService.selectAll('.bokeh-legend')[indexValue];
          legendEle.style.width = chartWidth;
          this.customRendererService.appendNode(ele, legendEle);
        });
      });
    }, 500);
  }

  private postRenderChartLayout(chart: any, data: any, scaleText = 1) {
    let font10pt = 10;
    let font12pt = 12;
    if (scaleText != null && scaleText !== 1) {
      font10pt = font10pt * scaleText;
      font12pt = font12pt * scaleText;
    }

    chart.yaxis.axis_label = data['y_axis_label'] || data['yaxis.axis_label'];
    chart.yaxis.axis_label_text_font_size = font10pt + 'pt';
    chart.yaxis.axis_label_text_font_style = 'normal';
    chart.yaxis.axis_label_text_color = Defaults.Chart.AxisLabelTextColor;
    chart.yaxis.major_label_text_color = Defaults.Chart.AxisLabelTextColor;
    chart.xaxis.axis_label = data['x_axis_label'] || data['xaxis.axis_label'];
    chart.xaxis.axis_label_text_font_size = font10pt + 'pt';
    chart.xaxis.axis_label_text_font_style = 'normal';
    chart.xaxis.axis_label_text_color = Defaults.Chart.AxisLabelTextColor;
    chart.xaxis.major_label_text_color = Defaults.Chart.AxisLabelTextColor;

    if (chart.right) {
      _.each(chart.right, (right): void => {
        right.attributes.axis_label_text_font_size = font10pt + 'pt';
        right.attributes.axis_label_text_font_style = 'normal';
        right.attributes.axis_label_text_color = Defaults.Chart.AxisLabelTextColor;
        right.attributes.major_label_text_color = Defaults.Chart.AxisLabelTextColor;
      });
    }

    if (chart.left) {
      _.each(chart.left, (left): void => {
        left.attributes.axis_label_text_font_size = font10pt + 'pt';
        left.attributes.axis_label_text_font_style = 'normal';
        left.attributes.axis_label_text_color = Defaults.Chart.AxisLabelTextColor;
        left.attributes.major_label_text_color = Defaults.Chart.AxisLabelTextColor;
      });
    }

    //override chart title attributes
    chart.title.text = data['title_text'];
    chart.title.align = data['title_align'] || data['title.align'];
    chart.title.text_font_size = font12pt + 'pt';
    chart.title.text_color = Defaults.Chart.TitleColor;

    //override chart legend attributes
    chart._legend.location = 'top_left';
    chart._legend.background_fill_color = Defaults.Chart.BackgroundFillColor;
    chart._legend.border_line_width = Defaults.Legend.BorderWidth;
    chart._legend.background_fill_alpha = Defaults.Legend.BackgroundFillAlpha;
    chart._legend.label_text_color = Defaults.LabelTextColor;

    if (scaleText != null && scaleText !== 1) {
      chart._legend.label_text_font_size = font10pt + 'pt';
    }

    //override chart border attributes
    chart.min_border_left = 40;
    chart.min_border_right = 40;

    //remove grid lines in chart
    chart.xgrid.grid_line_color = Defaults.GridLineColor;
    chart.ygrid.grid_line_color = Defaults.GridLineColor;

    return chart;
  }
}
